import React, { useContext, useState } from 'react';

import gettext from '@eb/gettext';
import { Button } from '@eb/eds-button';
import logger from '@eb/logger-bugsnag';
import { withMainControls } from '@eb/eds-structure';

import { EventContext, OrganizerContext } from '../context';

import { DiscoveryEvent } from '../types';

interface ShowMoreEventsProps {
    showFuture: boolean;
}

interface ShowMoreEventsResponse {
    data: {
        events: DiscoveryEvent[];
        has_next_page: boolean;
    };
}

interface MainControlsProps {
    addMainNotification: Function;
}

const getMoreEvents = async (url: string) => {
    const response = await fetch(url);
    const responseData: ShowMoreEventsResponse = await response.json();

    return responseData;
};

const formatResponseForStore = (response: ShowMoreEventsResponse) => {
    const ids = response.data.events.map(({ id }) => id);
    const events = response.data.events.reduce((memo, event) => {
        return {
            ...memo,
            [event.id]: event,
        };
    }, {});

    return { ids, events };
};

export const ShowMoreEventsInternal: React.FunctionComponent<
    ShowMoreEventsProps & MainControlsProps
> = (props) => {
    const [isLoading, setLoading] = useState(false);
    const { id } = useContext(OrganizerContext);
    const { state, dispatch } = useContext(EventContext);

    let hasMore;
    let page: number;
    let eventType = 'future';

    if (props.showFuture) {
        hasMore = state?.future.hasMore;
        page = state?.future.currentPage as number;
    } else {
        hasMore = state?.past.hasMore;
        page = state?.past.currentPage as number;
        eventType = 'past';
    }

    if (!hasMore) {
        return null;
    }

    const handleClick = async () => {
        setLoading(true);

        try {
            const url = `/org/${id}/showmore/?type=${eventType}&page=${
                page + 1
            }`;
            const responseData = await getMoreEvents(url);
            const action = `add_${eventType}`;

            dispatch &&
                dispatch({
                    type: action,
                    payload: {
                        ...formatResponseForStore(responseData),
                        requestedPage: page + 1,
                        hasMore: responseData.data.has_next_page,
                    },
                });
        } catch (error) {
            logger.error('Error fetching organizer events:', {
                error: error.message ? error.message : error,
            });

            props.addMainNotification({
                type: 'error',
                children: gettext('Error fetching organizer events'),
                isNotificationSticky: true,
            });
        }

        setLoading(false);
    };

    return (
        <div className="eds-l-pad-top-4 eds-align--center">
            <Button isLoading={isLoading} onClick={() => handleClick()}>
                {gettext('Show more')}
            </Button>
        </div>
    );
};

export const ShowMoreEvents = withMainControls(ShowMoreEventsInternal);
